import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button, ButtonGroup, Alert } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Components
import GeneralSettings from "../../Components/Common/GeneralSettings";
import GroupsManager from "./components/GroupsManager";
import PlotContainer from "../../Components/Common/PlotContainer";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";

// Config
import {
  differentialExpressionConfig,
  differentialExpressionDefaults,
} from "../../config/plotConfigs/differentialExpressionConfig";

// API
import {
  getDifferentialPlotData,
  generateDifferentialExpressionPlot,
  fetchJobStatus,
} from "../../helpers/eirna_backend_helper";

// Custom hooks
import { useDifferentialSettings } from "./hooks/useDifferentialSettings";
import { useFileSelection } from "./hooks/useFileSelection";
import { useGroupsManager } from "./hooks/useGroupsManager";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  useDifferentialExpressionShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";
import ActionButton from "../../Components/Common/ActionButton";

const DifferentialExpression = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [loading, setLoading] = useState(true);
  const [plotLoading, setPlotLoading] = useState(false);
  const [plotData, setPlotData] = useState(null);
  const [error, setError] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  // References to plotContainer and loadingDiv, to allow for auto scrolling
  const loadingDivRef  = useRef(null);
  const plotContainerRef  = useRef(null);

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    default_tran: "",
    seq_types: [],
    studies_dict: {},
    subgroup_present: false,
    organism: "",
    transcriptome: "",
    html_args: {},
  });

  // Custom hooks
  const {
    settings,
    updateSetting,
    updateMultipleSettings,
    resetSettings,
    validateSettings,
  } = useDifferentialSettings();

  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    toggleFileWithShift,
    toggleAllFiles,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    selectMultipleFiles,
    deselectMultipleFiles,
    lastSelectedFileIndex,
    setLastSelectedFileIndex,
  } = useFileSelection("riboseq");

  const {
    groups,
    setGroups,
    addFilesToGroup,
    removeFilesFromGroup,
    updateGroupName,
    clearGroup,
    autoLabelGroups,
    clearAllGroups,
    validateGroupsForDE,
    getMasterFileDict,
  } = useGroupsManager();

  // Use the DifferentialExpression specific short code handler
  const {
    autoRunPlot,
    setAutoRunPlot,
    consumeUserPassedShortCode,
  } = useDifferentialExpressionShortCode({
    location,
    updateMultipleSettings,
    setGroups,
  });

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

    // Scroll to loadingDiv anytime plotLoading changes
  useScrollToRef(loadingDivRef, plotLoading);

  // Scroll to plot/table anytime plotData changes
  useScrollToRef(plotContainerRef, plotData);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getDifferentialPlotData(projectId);
        setApiData(response);

        const fileTypes = [
          ...new Set(response.json_files?.map((file) => file.file_type) || []),
        ];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }
      } catch (err) {
        setError("Failed to load project data");
        toast.error("Failed to load project data");
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId, setSelectedSeqType]);

  // Auto-trigger analysis generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {

      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        groupsWithFiles: groups.filter((g) => g.files.length > 0).length,
      });

      // Check if we have files in groups (either Ribo-Seq or RNA-Seq pairs)
      const riboGroup1 = groups.find((g) => g.id === "riboseq1");
      const riboGroup2 = groups.find((g) => g.id === "riboseq2");
      const rnaGroup1 = groups.find((g) => g.id === "rnaseq1");
      const rnaGroup2 = groups.find((g) => g.id === "rnaseq2");

      const hasRiboFiles =
        riboGroup1?.files.length > 0 && riboGroup2?.files.length > 0;
      const hasRnaFiles =
        rnaGroup1?.files.length > 0 && rnaGroup2?.files.length > 0;
      const hasValidGroups = hasRiboFiles || hasRnaFiles;

      if (!plotLoading && !loading && hasValidGroups) {
        console.log(
          "All conditions met, generating DifferentialExpression analysis from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGeneratePlot();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [
    autoRunPlot,
    plotLoading,
    loading,
    groups.map((g) => g.files.length).join(","),
    setAutoRunPlot,
  ]);

  // Filter files based on selected seq type and group
  const filteredFiles = useMemo(() => {
    let files = apiData.json_files || [];
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((file) => file.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((file) => file.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  // Get unique seq types and groups
  const seqTypes = useMemo(() => {
    const types = [
      ...new Set(apiData.json_files?.map((file) => file.file_type) || []),
    ];
    return types.map((type) => ({
      value: type,
      label:
        type === "riboseq" ? "Ribo-Seq" : type === "rnaseq" ? "mRNA-Seq" : type,
      count:
        apiData.json_files?.filter((file) => file.file_type === type).length ||
        0,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );
    return Array.from(groupSet)
      .filter((group) => group)
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group)
            .length || 0,
      }));
  }, [apiData.json_files]);

  // Check if all files of selected seq type are selected (across ALL groups)
  const allSeqTypeFilesSelected = useMemo(() => {
    if (!selectedSeqType || selectedSeqType === "all") return false;
    // Use all files, not filtered files, and filter only by seq type
    const seqTypeFiles = (apiData.json_files || []).filter(
      (file) => file.file_type === selectedSeqType
    );
    return (
      seqTypeFiles.length > 0 &&
      seqTypeFiles.every((file) => selectedFiles.includes(file.file_id))
    );
  }, [selectedSeqType, apiData.json_files, selectedFiles]);

  // Check if all files of selected group are selected
  const allGroupFilesSelected = useMemo(() => {
    if (!selectedGroup || selectedGroup === "all") return false;
    const groupFiles = filteredFiles.filter(
      (file) => file.subgroup === selectedGroup
    );
    return (
      groupFiles.length > 0 &&
      groupFiles.every((file) => selectedFiles.includes(file.file_id))
    );
  }, [selectedGroup, filteredFiles, selectedFiles]);

  // Enhanced toggle file with shift-click support
  const handleToggleFile = (fileId) => {
    const fileIndex = filteredFiles.findIndex((f) => f.file_id === fileId);
    toggleFileWithShift(fileId, false, fileIndex, filteredFiles);
  };

  // Enhanced toggle file with event support for shift-click detection
  const handleToggleFileWithEvent = (e, fileId) => {
    const fileIndex = filteredFiles.findIndex((f) => f.file_id === fileId);
    toggleFileWithShift(fileId, e.shiftKey, fileIndex, filteredFiles);
  };

  // Handle auto-labeling with improved logic that preserves prefixes
  const handleAutoLabel = () => {
    // Use all files from API data to ensure file names are preserved
    autoLabelGroups(apiData.json_files || []);
  };

  // Handle select all seq type files (across ALL groups)
  const handleSelectAllSeqTypeFiles = useCallback(
    (checked) => {
      if (!selectedSeqType || selectedSeqType === "all") return;
      // Use all files, not filtered files, and filter only by seq type
      const seqTypeFiles = (apiData.json_files || []).filter(
        (file) => file.file_type === selectedSeqType
      );
      const seqTypeFileIds = seqTypeFiles.map((file) => file.file_id);

      if (checked) {
        selectMultipleFiles(seqTypeFileIds);
      } else {
        deselectMultipleFiles(seqTypeFileIds);
      }
    },
    [
      selectedSeqType,
      apiData.json_files,
      selectMultipleFiles,
      deselectMultipleFiles,
    ]
  );

  // Handle select all group files
  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;
      const groupFiles = filteredFiles.filter(
        (file) => file.subgroup === selectedGroup
      );
      const groupFileIds = groupFiles.map((file) => file.file_id);

      if (checked) {
        selectMultipleFiles(groupFileIds);
      } else {
        deselectMultipleFiles(groupFileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  // Task polling function (similar to updateProgressCommon)
  const pollTaskStatus = useCallback(
    async (taskId) => {
      try {
        const result = await fetchJobStatus(taskId);

        if (["SUCCESS", "FAILURE"].includes(result.state)) {
          setPlotLoading(false);
          setTaskId(null);

          if (result.state === "SUCCESS") {
            if (result.result.message === "user logged out") {
              navigate("/login");
              return;
            }

            if (result.result.message?.trim()) {
              setError(result.result.message);
              toast.error(result.result.message);
            } else if (result.result.plot) {
              setPlotData(result.result.plot);
              toast.success(
                "Differential expression plot generated successfully"
              );
            }
          } else {
            const errorMessage = `Please email ebconnect@eirnabio.com and mention this task ID: ${taskId}`;
            setError(errorMessage);
            toast.error(errorMessage);
          }
        } else {
          // Task still running, poll again
          setTimeout(() => pollTaskStatus(taskId), 1000);
        }
      } catch (err) {
        const errorMessage = `Please email ebconnect@eirnabio.com and mention this task ID: ${taskId}`;
        setError(errorMessage);
        toast.error(errorMessage);
        setPlotLoading(false);
        setTaskId(null);
      }
    },
    [navigate]
  );

  // Handle generate plot with updated payload structure
  const handleGeneratePlot = useCallback(async () => {
    // Validate general settings
    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    // Validate groups for DE analysis
    const groupValidation = validateGroupsForDE();
    if (!groupValidation.isValid) {
      toast.error(groupValidation.errors[0]);
      return;
    }

    try {
      setPlotLoading(true);
      setError(null);
      setPlotData(null);
      const shortCodeForThisRequest = consumeUserPassedShortCode();

      // Use the DifferentialExpression specific payload creator
      const plotRequest = createPlotSpecificPayload.differentialExpression({
        settings,
        projectId,
        userPassedShortCode: shortCodeForThisRequest,
        groups,
        getMasterFileDict,
        apiData,
      });

      // No email notification option for single DE jobs, set to false here to satisfy
      // pydantic model
      plotRequest.email_notification = false

      console.log("DE plot request:", plotRequest);

      const response = await generateDifferentialExpressionPlot(plotRequest);
      console.log("DE plot response:", response);

      // Handle task-based response
      if (response.task_id) {
        setTaskId(response.task_id);
        toast.info(`Job started with ID: ${response.task_id}`);
        pollTaskStatus(response.task_id);
      } else {
        // Handle direct response
        if (response.message === "user logged out") {
          navigate("/login");
          return;
        }

        if (response.message?.trim()) {
          setError(response.message);
          toast.error(response.message);
        } else if (response.plot) {
          setPlotData(response.plot);
          toast.success("Differential expression plot generated successfully");
        }
        setPlotLoading(false);
      }
    } catch (err) {
      let errorMsg = "Failed to generate differential expression plot";
      if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      console.error("DE plot error:", err);
      setError(errorMsg);
      toast.error(errorMsg);
      setPlotLoading(false);
      setTaskId(null);
    }
  }, [
    settings,
    validateSettings,
    validateGroupsForDE,
    getMasterFileDict,
    apiData.json_files,
    groups,
    projectId,
    navigate,
    pollTaskStatus,
    consumeUserPassedShortCode,
  ]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <FadeLoader color="#2F9E37" />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Differential Expression | EIRNA Bio Connect";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          breadcrumbs={[
            { label: "Home", url: "/dashboard" },
            {
              label: "Select Function",
              url: `/projects/${projectId}/select-function`,
            },
            { label: "Differential Expression" },
          ]}
        />

        <Row className="mb-4">
          <Col lg={12}>
            {/* General Settings */}
            <GeneralSettings
              settings={settings}
              onUpdateSetting={updateSetting}
              onReset={resetSettings}
              config={differentialExpressionConfig}
              title="Differential Expression Settings"
            />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col lg={12}>
            {/* Seq Types and Groups */}
            <SeqTypesAndGroups
              seqTypes={seqTypes}
              groups={availableGroups}
              selectedSeqType={selectedSeqType}
              selectedGroup={selectedGroup}
              onSelectSeqType={setSelectedSeqType}
              onSelectGroup={setSelectedGroup}
              // File selection props
              onSelectAllSeqTypeFiles={handleSelectAllSeqTypeFiles}
              onSelectAllGroupFiles={handleSelectAllGroupFiles}
              selectedFiles={selectedFiles}
              filteredFiles={filteredFiles}
              allSeqTypeFilesSelected={allSeqTypeFilesSelected}
              allGroupFilesSelected={allGroupFilesSelected}
              // Configuration
              showSelectAllCheckboxes={false}
              customTitle="Seq Types & Groups"
              customIcon="ri-tools-fill"
            />
          </Col>
        </Row>

        <Row className="mb-4">
          <Col lg={12}>
            {/* Groups Manager */}
            <GroupsManager
              groups={groups}
              files={filteredFiles}
              allFiles={apiData.json_files}
              onToggleFile={handleToggleFile}
              onToggleFileWithEvent={handleToggleFileWithEvent}
              onToggleAll={toggleAllFiles}
              onAddFilesToGroup={addFilesToGroup}
              onRemoveFilesFromGroup={removeFilesFromGroup}
              onUpdateGroupName={updateGroupName}
              onClearGroup={clearGroup}
              onAutoLabel={handleAutoLabel}
              onClearAll={clearAllGroups}
              groupLabels={{
                    group1: groups[0]?.name || "Group 1",
                    group2: groups[1]?.name || "Group 2",
                    group3: groups[2]?.name || "Group 3",
                    group4: groups[3]?.name || "Group 4",
                  }}
            />

            {/* Plot Container */}
          </Col>
        </Row>
        <Row className="mb-4">
          <Col lg={12} className="text-end">
            <div ref={loadingDivRef} 
                 className="d-flex justify-content-between align-items-center">
            </div>
            <ActionButton
              onClick={handleGeneratePlot}
              loading={plotLoading}
              buttonType="plot"
            />{" "}
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <PlotContainer
              ref={plotContainerRef}
              plotData={plotData}
              loading={plotLoading}
              error={error}
              selectedFilesCount={groups.reduce(
                (sum, g) => sum + g.files.length,
                0
              )}
              title="Differential Expression Results"
              showFileCount={true}
              showDownloadOptions={true}
            />
          </Col>
        </Row>
        <ToastContainer />
      </Container>
    </div>
  );
};

export default DifferentialExpression;
